<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-arrow-return-left me-2"></i>Supplier Returns Management
      </h4>
      <div class="d-flex">
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="returnModal = true; returnMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Supplier Return
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading supplier returns...</p>
      </div>

      <div v-if="!loading" class="returns-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ returns.total }}</span>
            <span class="text-muted">Supplier Returns</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search return" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>STORE</th>
                  <th>SUPPLIER</th>
                  <th>BY USER</th>
                  <th>DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(returnItem, index) in returns.data" :key="returnItem.id" class="return-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ returnItem.id.split("-").pop() }}</td>
                  <td>{{ returnItem.code }}</td>
                  <td>{{ returnItem.store?.name || '-' }}</td>
                  <td>{{ returnItem.supplier?.name || '-' }}</td>
                  <td>{{ returnItem.user?.first_name }} {{ returnItem.user?.last_name }}</td>
                  <td>{{ returnItem.description || '-' }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(returnItem.status)">
                      {{ returnItem.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(returnItem.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="returnDetails(returnItem.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <Modal :open="returnModal" @closed="returnModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="returnMode === 'add'">
          <i class="bi bi-plus-circle me-2"></i>Add Supplier Return
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2"></i>Edit Supplier Return
        </h5>
        <button @click="returnModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="code" class="form-label fw-bold">Return Code:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-upc"></i></span>
                  <input type="text" class="form-control" placeholder="Code" v-model="supplierReturn.code" readonly>
                </div>
                <small class="form-text text-muted">Auto-generated code</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="store" class="form-label fw-bold">Store:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-shop"></i></span>
                  <input type="text" class="form-control" :value="store.name" disabled>
                </div>
                <small class="form-text text-muted">Current store</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group mb-3">
                <label for="supplier" class="form-label fw-bold">Supplier:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-truck"></i></span>
                  <select class="form-select" v-model="supplierReturn.supplier_id" data="">
                    <option value="" disabled selected>Select supplier</option>
                    <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
                  </select>
                </div>
                <small class="form-text text-muted">Select the supplier to return items to</small>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12">
              <div class="form-group mb-3">
                <label for="description" class="form-label fw-bold">Description:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-card-text"></i></span>
                  <textarea class="form-control" placeholder="Enter return description" v-model="supplierReturn.description" rows="3"></textarea>
                </div>
                <small class="form-text text-muted">Provide details about this return</small>
              </div>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col-12">
              <div class="alert alert-info d-flex align-items-center">
                <i class="bi bi-info-circle-fill me-2"></i>
                <div>
                  <strong>Note:</strong> After creating the return, you'll be able to add products to it.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="returnModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="returnMode === 'add'" @click="createReturn">
          <i class="bi bi-plus-circle me-1"></i> Create Return
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateReturn(supplierReturn.id)">
          <i class="bi bi-check-circle me-1"></i> Update Return
        </button>
      </div>
    </Modal>

  </Card>
</template>

<script>
import supplierReturnService from "@/api/supplierReturnService";
import supplierService from "@/api/supplierService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";

export default {
  name: "SupplierReturns",

  data(){
    const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    const store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    const organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: organization,
      store: store,
      suppliers: [],
      profile: profile,
      loading: true,
      supplierReturn: {
        organization_id: organization.id,
        store_id: store.id,
        supplier_id: '',
        user_id: profile.id,
        code: '',
        description: '',
      },
      returnMode: 'add',
      returnModal: false,
      returns: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatDate,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getSupplierReturns() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.returns = await supplierReturnService.getSupplierReturns(params)
      this.loading = false
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getSupplierReturns()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getSupplierReturns()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    returnDetails(id) {
      router.push('/supplier-return/' + id)
    },
    async createReturn() {
      let response = await supplierReturnService.createSupplierReturn(this.supplierReturn)
      if (response) {
        this.returnModal = false
        router.push('/supplier-return/' + response.id)
      }
    },
  },

  computed: {
    totalPages(){
      return this.returns?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getSupplierReturns()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getSupplierReturns()
    },
    async returnModal(val) {
      if (val) {
        let nextNo = await supplierReturnService.getSupplierReturnNextNo()
        this.supplierReturn.code = nextNo.next
      }
    }
  },

  created: async function () {
    await this.getSupplierReturns()
    const suppliersData = await supplierService.getSuppliers({ per_page: 1000 })
    this.suppliers = Array.isArray(suppliersData?.data) ? suppliersData.data.filter(s => s != null) : []
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
.returns-content {
  padding: 0.5rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.return-row {
  transition: all 0.2s ease;
}

.return-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
